/**
 * <hidden-scroll-gallery> — two image strips translating horizontally in
 * opposite directions, driven purely by scroll progress (gate 2026-07-29:
 * scroll-driven only, no idle drift). Hover zoom is CSS. Rows are
 * [data-gallery-row] with data-direction="1" | "-1".
 * Skipped under prefers-reduced-motion and in the theme editor.
 */
class HiddenScrollGallery extends HTMLElement {
  #raf = 0;

  /** @type {(() => void) | null} */
  #onScroll = null;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.Shopify?.designMode) return;
    this.#onScroll = () => {
      if (this.#raf) return;
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#update();
      });
    };
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    if (this.#onScroll) {
      window.removeEventListener('scroll', this.#onScroll);
      window.removeEventListener('resize', this.#onScroll);
    }
    cancelAnimationFrame(this.#raf);
  }

  #update() {
    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight;
    // 0 when the gallery enters from the bottom, 1 when it leaves at the top.
    const progress = Math.min(1, Math.max(0, (viewport - rect.top) / (viewport + rect.height)));
    const shift = (progress - 0.5) * 2; // -1 .. 1 through the journey
    for (const row of this.querySelectorAll('[data-gallery-row]')) {
      if (!(row instanceof HTMLElement)) continue;
      const direction = Number(row.dataset.direction || 1);
      const max = row.scrollWidth * 0.08; // gentle travel, rows are wider than the viewport
      row.style.transform = `translateX(${(shift * max * direction).toFixed(1)}px)`;
    }
  }
}

if (!customElements.get('hidden-scroll-gallery')) {
  customElements.define('hidden-scroll-gallery', HiddenScrollGallery);
}
